import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { CalendarDay } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import { Field, Input } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'
import { cn } from '@/lib/cn'
import { currentMonthInMalaysia, todayInMalaysia } from '@/lib/date'
import SectionLoad from '@/pages/admin/capacity/SectionLoad'

interface CapacityData {
  default_daily_limit: number
  /** ISO weekdays, 1 = Monday ... 7 = Sunday. */
  working_weekdays: number[]
  month: string
  days: CalendarDay[]
}

const WEEKDAYS = [
  { iso: 1, label: 'Mon' },
  { iso: 2, label: 'Tue' },
  { iso: 3, label: 'Wed' },
  { iso: 4, label: 'Thu' },
  { iso: 5, label: 'Fri' },
  { iso: 6, label: 'Sat' },
  { iso: 7, label: 'Sun' },
]

const pill = (active: boolean) =>
  cn(
    'rounded-lg border px-3.5 py-2 text-sm font-semibold',
    active ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:bg-foreground/5',
  )

/**
 * Two views of capacity. Intake is how many pieces may START production on a
 * day, plus which days the factory works. Section load is where that work is
 * booked afterwards - each section against its own daily capacity. The tab
 * lives in the URL so the jobs list can link straight to Section load.
 */
export default function AdminCapacity() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'sections' ? 'sections' : 'intake'

  return (
    <div>
      <PageHeader
        title="Capacity"
        subtitle={
          tab === 'intake'
            ? 'How many pieces production can start each day, and which days the factory works'
            : 'Pieces booked into each section per day, against its daily capacity'
        }
      />

      <div className="mb-4 flex flex-wrap gap-1.5">
        <button type="button" className={pill(tab === 'intake')} onClick={() => setParams({}, { replace: true })}>
          Intake
        </button>
        <button type="button" className={pill(tab === 'sections')} onClick={() => setParams({ tab: 'sections' }, { replace: true })}>
          Section load
        </button>
      </div>

      {tab === 'sections' ? <SectionLoad /> : <IntakeTab />}
    </div>
  )
}

function IntakeTab() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [month, setMonth] = useState(currentMonthInMalaysia)
  const [defaultLimit, setDefaultLimit] = useState<string | null>(null)
  // Unsaved weekday toggles; null means "as stored".
  const [weekdays, setWeekdays] = useState<number[] | null>(null)
  const [editing, setEditing] = useState<CalendarDay | null>(null)
  const [overrideLimit, setOverrideLimit] = useState('')
  const [blocked, setBlocked] = useState(false)
  const [note, setNote] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['admin-capacity', month],
    queryFn: async () => (await api.get<CapacityData>('/admin/capacity', { params: { month } })).data,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-capacity'] })
    queryClient.invalidateQueries({ queryKey: ['capacity'] })
    queryClient.invalidateQueries({ queryKey: ['section-load'] })
  }

  const saveDefault = useMutation({
    mutationFn: async () =>
      (await api.patch('/admin/capacity/default', { default_daily_limit: Number(defaultLimit) })).data,
    onSuccess: (res) => {
      invalidate()
      toast.success(res.message)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const saveWorkingDays = useMutation({
    mutationFn: async (days: number[]) =>
      (await api.patch<{ message: string; stranded_jobs: number }>('/admin/capacity/working-days', { working_weekdays: days })).data,
    onSuccess: (res) => {
      invalidate()
      setWeekdays(null)
      // Work already booked on a day that is now closed is never moved -
      // make sure the admin notices rather than reading a plain "saved".
      if (res.stranded_jobs > 0) toast.info('Working days saved', res.message)
      else toast.success(res.message)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const saveOverride = useMutation({
    mutationFn: async () =>
      (
        await api.put('/admin/capacity/overrides', {
          date: editing!.date,
          limit: overrideLimit === '' ? null : Number(overrideLimit),
          is_blocked: blocked,
          note: note || null,
        })
      ).data,
    onSuccess: (res) => {
      invalidate()
      toast.success(res.message)
      setEditing(null)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const openDay = (day: CalendarDay) => {
    setEditing(day)
    setOverrideLimit('')
    setBlocked(day.blocked)
    setNote('')
  }

  const today = todayInMalaysia()

  if (isLoading || !data) return <LogoLoader />

  const selectedDays = weekdays ?? data.working_weekdays
  const toggleWeekday = (iso: number) =>
    setWeekdays(
      selectedDays.includes(iso) ? selectedDays.filter((d) => d !== iso) : [...selectedDays, iso].sort((a, b) => a - b),
    )

  return (
    <div>
      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <SectionLabel>Working days</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map((day) => {
              const on = selectedDays.includes(day.iso)
              return (
                <button
                  key={day.iso}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleWeekday(day.iso)}
                  className={cn(
                    'min-w-12 rounded-lg border px-3 py-2 text-sm font-semibold',
                    on ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:bg-foreground/5',
                  )}
                >
                  {day.label}
                </button>
              )
            })}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted">
              Production dates, section bookings and delivery due dates only use ticked days. Work already booked on a
              day you close stays where it is.
            </p>
            <Button
              disabled={weekdays === null || selectedDays.length === 0}
              loading={saveWorkingDays.isPending}
              onClick={() => saveWorkingDays.mutate(selectedDays)}
            >
              Save
            </Button>
          </div>
        </Card>

        <Card>
          <SectionLabel>Default daily intake</SectionLabel>
          <div className="flex max-w-sm gap-2">
            <Input
              type="number"
              value={defaultLimit ?? String(data.default_daily_limit)}
              onChange={(e) => setDefaultLimit(e.target.value)}
            />
            <Button loading={saveDefault.isPending} onClick={() => saveDefault.mutate()}>
              Save
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted">
            Pieces that can start production per day, e.g. 1,200. Click any date below to override or block it.
          </p>
        </Card>
      </div>

      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <SectionLabel className="mb-0">Booked vs intake</SectionLabel>
          <div className="flex items-center gap-2">
            <Button variant="ghost" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>
              <ChevronLeft className="size-4" />
            </Button>
            <span className="text-sm font-semibold">{month}</span>
            <Button variant="ghost" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}>
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {WEEKDAYS.map((d) => (
            <div key={d.iso} className="pb-1 text-center text-2xs font-bold uppercase text-muted">
              {d.label}
            </div>
          ))}
          {paddingFor(data.days[0].date).map((_, i) => (
            <div key={`pad-${i}`} />
          ))}
          {data.days.map((day) => {
            const pct = day.limit ? Math.min(100, Math.round((day.booked / day.limit) * 100)) : 0
            const isToday = day.date === today
            return (
              <button
                key={day.date}
                onClick={() => openDay(day)}
                aria-current={isToday ? 'date' : undefined}
                className={cn(
                  'flex min-h-14 flex-col rounded-lg border p-1 text-left transition-colors hover:border-brand-400 sm:min-h-20 sm:p-1.5',
                  day.blocked ? 'border-danger/40 bg-danger/10' : 'border-line bg-surface',
                  !day.working && !day.blocked && 'bg-app',
                  isToday && 'ring-2 ring-primary',
                )}
              >
                <span className={cn('text-sm font-bold', isToday && 'text-primary')}>{Number(day.date.slice(8))}</span>
                {/* Below sm the labels hide - the tinted cell marks closed days
                    and the bar carries utilization; the tap-through modal shows
                    the exact numbers. */}
                {day.blocked ? (
                  <span className="mt-auto hidden text-2xs font-bold text-danger sm:inline">CLOSED</span>
                ) : !day.working ? (
                  <span className="mt-auto hidden text-2xs text-muted sm:inline">closed</span>
                ) : (
                  <>
                    <span className="mt-auto hidden text-2xs font-semibold sm:inline">
                      {day.booked}/{day.limit}
                    </span>
                    <div className="mt-auto h-1.5 overflow-hidden rounded-full bg-ink/10 sm:mt-1">
                      <div
                        className={cn('h-full rounded-full', pct >= 100 ? 'bg-danger' : pct >= 75 ? 'bg-warning' : 'bg-success')}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </>
                )}
              </button>
            )
          })}
        </div>
      </Card>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={`Override ${editing?.date}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button loading={saveOverride.isPending} onClick={() => saveOverride.mutate()}>
              Save override
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {editing && !editing.blocked && (
            <p className="text-sm text-muted">
              Booked <span className="font-semibold text-ink">{editing.booked}</span> of {editing.limit} pcs.
            </p>
          )}
          <Field label={`Custom limit for this date (blank = default ${data.default_daily_limit})`}>
            <Input
              type="number"
              value={overrideLimit}
              onChange={(e) => setOverrideLimit(e.target.value)}
              placeholder={String(data.default_daily_limit)}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={blocked} onChange={(e) => setBlocked(e.target.checked)} className="size-4 accent-brand-500" />
            Block this date (factory closed / public holiday)
          </label>
          <Field label="Note (optional)">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Hari Raya" />
          </Field>
        </div>
      </Modal>
    </div>
  )
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number)
  const date = new Date(y, m - 1 + delta, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function paddingFor(firstDate: string): unknown[] {
  const day = new Date(firstDate + 'T00:00:00').getDay()
  return Array.from({ length: (day + 6) % 7 })
}
